/**
 * GraphMinimap — Small overview of the full graph with viewport indicator.
 * R-CANVAS-05: Shows when graph exceeds ~30 nodes, helps with orientation.
 * Renders a simplified dot-based view of the graph and highlights the current viewport.
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import type cytoscape from 'cytoscape';
import { Map, X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface GraphMinimapProps {
    cy: cytoscape.Core | null;
    nodeCount: number;
    /** Minimum nodes before minimap auto-shows */
    threshold?: number;
    /**
     * Parent owns layout (e.g. stacked under the shortcuts icon). Keeps default
     * corner positioning when false; does not let className wipe absolute placement.
     */
    embedded?: boolean;
    className?: string;
}

const MINIMAP_W = 160;
const MINIMAP_H = 100;

function readCanvasColors(isDark: boolean) {
    const root = document.documentElement;
    const canvasSurface = getComputedStyle(root).getPropertyValue('--canvas-surface').trim();
    const gridDot = getComputedStyle(root).getPropertyValue('--canvas-grid-dot').trim();
    return {
        background: canvasSurface ? `hsla(${canvasSurface.replace(/ /g, ', ')}, 0.92)` : (isDark ? 'rgba(22, 23, 26, 0.92)' : 'rgba(248, 250, 252, 0.9)'),
        edgeStroke: gridDot ? `hsla(${gridDot.replace(/ /g, ', ')}, 0.55)` : (isDark ? 'rgba(82, 82, 91, 0.5)' : 'rgba(203, 213, 225, 0.5)'),
        viewportStroke: isDark ? 'rgba(99, 102, 241, 0.8)' : 'rgba(59, 130, 246, 0.8)',
        viewportFill: isDark ? 'rgba(99, 102, 241, 0.08)' : 'rgba(59, 130, 246, 0.05)',
        selectedStroke: isDark ? '#6366f1' : '#3b82f6',
    };
}

export const GraphMinimap: React.FC<GraphMinimapProps> = ({ cy, nodeCount, threshold = 30, embedded = false, className }) => {
    const theme = useTheme();
    const isDark = theme === 'dark';
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [dismissed, setDismissed] = useState(false);
    const animFrameRef = useRef<number>(0);

    const draw = useCallback(() => {
        if (!cy || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const nodes = cy.nodes();
        if (nodes.length === 0) return;

        // Get bounding box of all nodes
        const bb = cy.elements().boundingBox();
        if (bb.w === 0 || bb.h === 0) return;

        const pad = 10;
        const scaleX = (MINIMAP_W - 2 * pad) / bb.w;
        const scaleY = (MINIMAP_H - 2 * pad) / bb.h;
        const scale = Math.min(scaleX, scaleY);

        const offsetX = pad + ((MINIMAP_W - 2 * pad) - bb.w * scale) / 2;
        const offsetY = pad + ((MINIMAP_H - 2 * pad) - bb.h * scale) / 2;

        const toMini = (x: number, y: number) => ({
            x: (x - bb.x1) * scale + offsetX,
            y: (y - bb.y1) * scale + offsetY,
        });

        // Clear
        ctx.clearRect(0, 0, MINIMAP_W, MINIMAP_H);

        // Background
        const colors = readCanvasColors(isDark);
        ctx.fillStyle = colors.background;
        ctx.fillRect(0, 0, MINIMAP_W, MINIMAP_H);

        // Draw edges as thin lines
        ctx.strokeStyle = colors.edgeStroke;
        ctx.lineWidth = 0.5;
        cy.edges().forEach(edge => {
            const src = edge.source().position();
            const tgt = edge.target().position();
            const a = toMini(src.x, src.y);
            const b = toMini(tgt.x, tgt.y);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
        });

        // Draw nodes as dots
        nodes.forEach(node => {
            const pos = node.position();
            const p = toMini(pos.x, pos.y);
            const riskScore = node.data('risk_score') ?? 0;
            const isSelected = node.data('id') === cy.data('selectedNodeId');

            // Color by risk
            if (riskScore > 0.7) ctx.fillStyle = '#ef4444';
            else if (riskScore > 0.3) ctx.fillStyle = '#f59e0b';
            else ctx.fillStyle = '#22c55e';

            // Size
            const r = isSelected ? 3 : 1.5;

            ctx.beginPath();
            ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
            ctx.fill();

            if (isSelected) {
                ctx.strokeStyle = colors.selectedStroke;
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        });

        // Draw viewport rectangle
        const ext = cy.extent();
        const topLeft = toMini(ext.x1, ext.y1);
        const botRight = toMini(ext.x2, ext.y2);
        const vw = Math.max(botRight.x - topLeft.x, 10);
        const vh = Math.max(botRight.y - topLeft.y, 10);

        ctx.strokeStyle = colors.viewportStroke;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(topLeft.x, topLeft.y, vw, vh);
        ctx.fillStyle = colors.viewportFill;
        ctx.fillRect(topLeft.x, topLeft.y, vw, vh);
    }, [cy, isDark]);

    // Redraw on viewport changes
    useEffect(() => {
        if (!cy) return;

        const redraw = () => {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = requestAnimationFrame(draw);
        };

        cy.on('viewport', redraw);
        cy.on('position', redraw);
        cy.on('add remove', redraw);

        // Initial draw
        redraw();

        return () => {
            cy.off('viewport', redraw);
            cy.off('position', redraw);
            cy.off('add remove', redraw);
            cancelAnimationFrame(animFrameRef.current);
        };
    }, [cy, draw]);

    // Click to pan
    const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!cy || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const bb = cy.elements().boundingBox();
        if (bb.w === 0 || bb.h === 0) return;

        const pad = 10;
        const scaleX = (MINIMAP_W - 2 * pad) / bb.w;
        const scaleY = (MINIMAP_H - 2 * pad) / bb.h;
        const scale = Math.min(scaleX, scaleY);
        const offsetX = pad + ((MINIMAP_W - 2 * pad) - bb.w * scale) / 2;
        const offsetY = pad + ((MINIMAP_H - 2 * pad) - bb.h * scale) / 2;

        const graphX = (clickX - offsetX) / scale + bb.x1;
        const graphY = (clickY - offsetY) / scale + bb.y1;

        const zoom = cy.zoom();
        const container = cy.container();
        if (!container) return;
        const cw = container.clientWidth / 2;
        const ch = container.clientHeight / 2;
        cy.animate({
            pan: { x: cw - graphX * zoom, y: ch - graphY * zoom },
            duration: 200,
        });
    }, [cy]);

    // Don't show if below threshold or dismissed
    if (nodeCount < threshold || dismissed || !cy) return null;

    return (
        <div
            className={[
                embedded ? 'relative z-10' : 'absolute bottom-4 right-4 z-10',
                className || '',
            ].filter(Boolean).join(' ')}
            data-tour="canvas-minimap"
        >            <div className="bg-card/95 backdrop-blur-md border border-border rounded-lg shadow-overlay overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-2 py-1 border-b border-border/40">
                    <div className="flex items-center gap-1">
                        <Map className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wider">Minimap</span>
                        <span className="text-[10px] text-muted-foreground">{nodeCount} nodes</span>
                    </div>
                    <button
                        onClick={() => setDismissed(true)}
                        className="p-0.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"
                        title="Hide minimap"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>
                <canvas
                    ref={canvasRef}
                    width={MINIMAP_W}
                    height={MINIMAP_H}
                    onClick={handleClick}
                    className="cursor-crosshair"
                    style={{ width: MINIMAP_W, height: MINIMAP_H }}
                />
            </div>
        </div>
    );
};
